(() => {
  const ELEMENT_NAME = 'minovia-delivery-promise';
  const TIME_ZONE = 'Europe/Amsterdam';
  const DEFAULT_WEEKDAY_CUTOFF = '21:00';
  const DEFAULT_FRIDAY_CUTOFF = '15:00';
  const UPDATE_INTERVAL_MS = 60_000;

  // Add date-specific delivery copy here later without changing the weekly rules.
  const DELIVERY_EXCEPTIONS = new Map();

  const DAY_INDEX = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  };

  const amsterdamFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  function parseCutoff(value, fallback) {
    const [hour, minute] = (value || fallback).split(':').map(Number);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return parseCutoff(fallback, '00:00');
    }

    return hour * 60 + minute;
  }

  function getAmsterdamDateParts(date = new Date()) {
    const parts = Object.fromEntries(
      amsterdamFormatter
        .formatToParts(date)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );

    return {
      day: DAY_INDEX[parts.weekday],
      minutes: Number(parts.hour) * 60 + Number(parts.minute),
      dateKey: `${parts.year}-${parts.month}-${parts.day}`,
    };
  }

  function getDeliveryCopy(date, weekdayCutoff, fridayCutoff) {
    const { day, minutes, dateKey } = getAmsterdamDateParts(date);
    const exceptionCopy = DELIVERY_EXCEPTIONS.get(dateKey);

    if (exceptionCopy) return exceptionCopy;

    if (day >= 1 && day <= 4) {
      return minutes < weekdayCutoff
        ? 'Voor 21:00 besteld, morgen in huis'
        : 'Morgen verzonden';
    }

    if (day === 5) {
      return minutes < fridayCutoff
        ? 'Voor 15:00 besteld, morgen in huis'
        : 'Maandag verzonden';
    }

    return 'Maandag verzonden';
  }

  function getNextDayUspCopy(date, weekdayCutoff, fridayCutoff) {
    const { day, minutes } = getAmsterdamDateParts(date);

    if (day >= 1 && day <= 4) {
      return minutes < weekdayCutoff
        ? {
            title: 'Voor 21:00 besteld',
            subtitle: 'morgen in huis',
          }
        : {
            title: 'Morgen verzonden',
            subtitle: '',
          };
    }

    if (day === 5) {
      return minutes < fridayCutoff
        ? {
            title: 'Voor 15:00 besteld',
            subtitle: 'morgen in huis',
          }
        : {
            title: 'Maandag verzonden',
            subtitle: '',
          };
    }

    return {
      title: 'Maandag verzonden',
      subtitle: '',
    };
  }

  function updateNextDayUsps(date, deliveryAvailable) {
    document.querySelectorAll('[data-minovia-next-day-usp]').forEach((usp) => {
      const weekdayCutoff = parseCutoff(usp.dataset.weekdayCutoff, DEFAULT_WEEKDAY_CUTOFF);
      const fridayCutoff = parseCutoff(usp.dataset.fridayCutoff, DEFAULT_FRIDAY_CUTOFF);
      const copy = deliveryAvailable
        ? getNextDayUspCopy(date, weekdayCutoff, fridayCutoff)
        : null;
      const isVisible = Boolean(copy);

      usp.hidden = !isVisible;
      usp.closest('[data-minovia-product-usps]')?.classList.toggle(
        'has-hidden-delivery-usp',
        !isVisible
      );

      if (!copy) return;

      const title = usp.querySelector('[data-minovia-next-day-usp-title]');
      const subtitle = usp.querySelector('[data-minovia-next-day-usp-subtitle]');

      if (title) title.textContent = copy.title;
      if (subtitle) subtitle.textContent = copy.subtitle;
    });
  }

  class MinoviaDeliveryPromise extends HTMLElement {
    constructor() {
      super();
      this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    }

    connectedCallback() {
      if (this.updateTimer) return;

      this.copyElement = this.querySelector('[data-delivery-promise-copy]');
      this.productSection = this.closest('.shopify-section, dialog');
      this.weekdayCutoff = parseCutoff(this.dataset.weekdayCutoff, DEFAULT_WEEKDAY_CUTOFF);
      this.fridayCutoff = parseCutoff(this.dataset.fridayCutoff, DEFAULT_FRIDAY_CUTOFF);

      this.productSection?.addEventListener('variant:update', this.handleVariantUpdate);
      this.setAvailability(this.dataset.available === 'true');
      this.updateCopy();

      this.updateTimer = window.setInterval(() => {
        this.updateCopy();
      }, UPDATE_INTERVAL_MS);
    }

    disconnectedCallback() {
      this.productSection?.removeEventListener('variant:update', this.handleVariantUpdate);

      if (this.updateTimer) {
        window.clearInterval(this.updateTimer);
        this.updateTimer = undefined;
      }
    }

    handleVariantUpdate(event) {
      const eventProductId = event.detail?.data?.productId;
      const newProductId = event.detail?.data?.newProduct?.id;

      if (eventProductId && this.dataset.productId && String(eventProductId) !== this.dataset.productId) {
        return;
      }

      if (newProductId) {
        this.dataset.productId = String(newProductId);
      }

      this.setAvailability(Boolean(event.detail?.resource?.available));
      this.updateCopy();
    }

    setAvailability(available) {
      this.dataset.available = String(available);
      this.hidden = !available;
    }

    updateCopy(date = new Date()) {
      const deliveryAvailable = this.dataset.available === 'true';

      updateNextDayUsps(date, deliveryAvailable);

      if (!deliveryAvailable || !this.copyElement) return;

      this.copyElement.textContent = getDeliveryCopy(
        date,
        this.weekdayCutoff,
        this.fridayCutoff
      );
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, MinoviaDeliveryPromise);
  }
})();
